<template>
    <div ref="container" class="map is-overlay">
    </div>
</template>

<script>
import Vue from 'vue';
import TTMixin from '../mixins/TTMixin';

/**
 * Component which handles communication with PanoViewer component. It's
 * existence allows for separation of side effects related to panorama
 * manipulation. This component must not contain any references to Vuex
 * store.
 *
 * This component emits the following events:
 * * `selected` - when user clicks on the other panorama points, the
 *   coordinates of clicked point are passed,
 * * `orientationChanged` - when the camera orientation is changed, the
 *   object with `fov`, `yaw` and `pitch` properties is passed,
 * * `loadBegin` - when the panorama starts loading new scene,
 * * `loadEnd` - when the panorama successfully loaded low res
 *   thumbnail.
 */
export default {
    mixins: [TTMixin],

    props: {
        /**
         * Data describing panorama to display.
         */
        panorama: {
            type: Object,
            required: true,
        },

        /**
         * Initial orientation of the camera. Should consist of `fov`,
         * `pitch` and `yaw` properties, all in degrees. If `undefined`,
         * the orientation will be computed based on the camera heading.
         */
        initialOrientation: {
            type: Object,
            required: false,
        },

        /**
         * Holds the gamma correction factor for the panorama.
         */
        gamma: {
            type: Number,
            required: false,
            default: 0,
        },
    },

    /**
     * Whenever panorama component is activated (Vue hook),
     * the canvas is fit to the container size.
     */
    activated() {
        this.fit();
    },

    mounted() {
        const { PanoViewer } = window;
        const { selected } = this.panorama;

        /**
         * Initialization of `PanoViewer` object, with callback
         * emitting selected coords. Additionally, loadBegin and loadEnd
         * are emitted during panorama loading in order to show spinner.
         */
        const {
            container,
            controls: {
                setScene, refresh,
                zoomIn, zoomOut,
                setGamma,
            },
        } = PanoViewer({
            initial: selected,
            initialOrientation: this.initialOrientation,
            callbacks: {
                onSelect: (coords) => {
                    this.$emit('selected', coords);
                },
                // Show panorama loader when the panorama is
                // being loaded. When loading ends, hide it.
                onLoadBegin: () => { this.$emit('loadBegin'); },
                onLoadEnd: () => { this.$emit('loadEnd'); },
                onOrientationChange: (orientation) => {
                    this.$emit('orientationChanged', orientation);
                },
            },
        });

        container.style.width = '100%';
        container.style.height = '100%';
        container.style.position = 'absolute';

        this.container = container;
        this.setScene = setScene;
        this.zoomIn = zoomIn;
        this.zoomOut = zoomOut;
        this.fit = refresh;
        this.setGamma = setGamma;

        this.$refs.container.appendChild(container);

        /**
         * Whenever window is resized, fit panorama to window.
         */
        window.addEventListener('resize', this.fit.bind(this));

        /**
         * Fitting panorama view to window after panorama initialization.
         */
        this.fit();
    },

    methods: {
        /**
         * Updates rendered panorama in response to props change.
         */
        updateScene() {
            const { nearest, selected } = this.panorama;
            const {
                sequenceNumber: sequencenumber,
                sessionName: sessionname,
            } = selected;

            const params = { sessionname, sequencenumber };
            const tileUrl = this.momaURLBuilder(
                'momadata/GetPanorama',
                { ...params, zoomlevel: 0 },
            );

            this.setScene({
                hasThumbnailUrl: this.momaURLBuilder(
                    'momadata/IsPanoramaAnonymized',
                    params,
                ),
                thumbnailUrl: this.momaURLBuilder(
                    'momadata/GetPanorama',
                    { ...params, zoomlevel: 2 },
                ),
                anonymizedUrl: this.momaURLBuilder(
                    'momadata/GetPanorama',
                    { ...params, zoomlevel: 2, placeholder: true },
                ),
                tileUrl: `${tileUrl}&index=%i`,
                nearest,
                selected,
            });
        },
    },

    watch: {
        /**
         * Updates scene on any `panorama` prop change.
         * It makes this component reactive to `panorama` prop.
         */
        panorama: {
            handler() {
                Vue.nextTick(() => {
                    this.updateScene();
                });
            },
            immediate: true,
        },

        /**
         * Updates gamma correction factor in the panorama.
         */
        gamma: {
            handler(value) {
                Vue.nextTick(() => {
                    this.setGamma(value);
                });
            },
            immediate: true,
        },
    },
};
</script>
